// SPDX-License-Identifier: MIT
/**
 * editorial-lines (statements, 4–8 beats; reviewed in the Kestrel reel). One to three statements,
 * left-aligned and set tight, rise from behind their masks one line per half beat and leave
 * upwards through the same masks; accent segments are set in the brand colour. An optional quiet
 * column of label/value pairs decodes on the right. Copy is drawn in the crisp layer.
 */
import * as z from "zod/mini";
import { type ResolvedBrand, font, rgba } from '../brand.ts';
import * as kit from '../kit/index.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { displayTracking, sceneBeats } from './lib.ts';
const Segment = z.object({ text: z.string().check(z.maxLength(40)), accent: z.optional(z.boolean()) });
const Statement = z.object({ lines: z.array(z.array(Segment).check(z.minLength(1)).check(z.maxLength(4))).check(z.minLength(1)).check(z.maxLength(2)) });
type Seg = z.infer<typeof Segment>;
const X = 190;
const MAX_W = DESIGN_W - 2 * X;
const MAX_PX = 168;
const lineText = (segs: Seg[]) => segs.map((s) => s.text).join('');
function setFont(ctx: CanvasRenderingContext2D, brand: ResolvedBrand, text: string, px: number) {
    ctx.font = font(brand, 800, px);
    ctx.letterSpacing = displayTracking(text, px);
}
/** Largest size ≤ MAX_PX at which every line of the statement fits the measure. */
function statementSize(ctx: CanvasRenderingContext2D, brand: ResolvedBrand, lines: Seg[][]): number {
    let px = MAX_PX;
    const widest = () => Math.max(...lines.map((l) => {
        setFont(ctx, brand, lineText(l), px);
        return ctx.measureText(lineText(l)).width;
    }));
    for (let i = 0; i < 20 && widest() > MAX_W; i++)
        px *= 0.94;
    return px;
}
export default defineScene({
    id: 'editorial-lines',
    version: '1.0.0',
    aspects: ['16:9'],
    propsSchema: z.object({
        statements: z.array(Statement).check(z.minLength(1)).check(z.maxLength(3)),
        /** Optional right-hand column of [label, value] pairs (values must be facts if numeric). */
        log: z.optional(z
            .array(z.tuple([z.string().check(z.maxLength(18)), z.string().check(z.maxLength(10))])).check(z.maxLength(6))),
    }),
    glyphs: (p) => p.statements.flatMap((s) => s.lines.map(lineText)).join('') +
        (p.log ?? []).flat().join('') +
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*+<>/',
    motion: (_p, env) => (env.localBeat % 3 < 1 ? 0.8 : 0.3),
    audioEvents: (p) => (p.log ?? []).map((_, i) => ({ beat: 0.75 + i * 0.5, sfx: 'tick' as const, gain: 0.7 })),
    post: (_p, env) => ({ bloom: env.style.background === 'dark' ? 0.8 : 0.4 }),
    draw(ctx, props, env) {
        const th = kit.theme(env);
        const L = sceneBeats(env);
        ctx.fillStyle = th.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        kit.fx.pool(ctx, X + 300, DESIGN_H / 2, 1100, th.primary, th.dark ? 0.05 : 0.03);
        kit.camera.handheld(ctx, env.t, 3);
        const n = props.statements.length;
        const slot = L / n;
        // Sizes and the widest line are fixed per statement, so layout never jumps mid-scene.
        const sizes = props.statements.map((st) => statementSize(ctx, env.brand, st.lines));
        const widest = Math.max(...props.statements.flatMap((st, si) => st.lines.map((l) => {
            setFont(ctx, env.brand, lineText(l), sizes[si]!);
            return ctx.measureText(lineText(l)).width;
        })));
        ctx.letterSpacing = '0px';
        env.crisp((c) => {
            c.textBaseline = 'alphabetic';
            props.statements.forEach((st, si) => {
                const px = sizes[si]!;
                const lh = px * 1.03;
                const inAt = si * slot;
                const last = si === n - 1;
                const outAt = last ? L - 0.55 : (si + 1) * slot - 0.35;
                const baseline = DESIGN_H / 2 - 10 - ((st.lines.length - 1) * lh) / 2 + px * 0.35;
                st.lines.forEach((segs, li) => {
                    const enter = kit.beat.seg(env, inAt + li * 0.4, inAt + li * 0.4 + 0.6, kit.curves.glide);
                    const exit = kit.beat.seg(env, outAt + li * 0.12, outAt + li * 0.12 + 0.4, kit.curves.launch);
                    if (enter <= 0 || exit >= 1)
                        return;
                    const lineBase = baseline + li * lh;
                    // Rises into place, then leaves upwards through the same mask.
                    const dy = exit > 0 ? -exit * lh * 1.05 : (1 - enter) * lh * 1.05;
                    c.save();
                    c.beginPath();
                    c.rect(0, lineBase - lh * 0.86, DESIGN_W, lh * 1.06);
                    c.clip();
                    let x = X;
                    for (const seg of segs) {
                        setFont(c, env.brand, lineText(segs), px);
                        c.fillStyle = seg.accent ? th.accentText : th.fg;
                        c.fillText(seg.text, x, lineBase + dy);
                        x += c.measureText(seg.text).width;
                    }
                    c.restore();
                });
            });
            c.letterSpacing = '0px';
        });
        // Speed streaks behind each statement's accent line as it lands (dark grounds only).
        if (th.dark)
            props.statements.forEach((st, si) => {
                const li = st.lines.findIndex((l) => l.some((s) => s.accent));
                if (li < 0)
                    return;
                const at = si * slot + li * 0.5;
                const k = kit.beat.seg(env, at + 0.1, at + 0.75, kit.curves.snap);
                if (k <= 0 || k >= 1)
                    return;
                const r = env.rng(`streak-${si}`);
                ctx.save();
                ctx.globalCompositeOperation = 'lighter';
                for (let s = 0; s < 9; s++) {
                    const y = DESIGN_H / 2 + 40 + (r() - 0.5) * 160;
                    const len = 300 + r() * 700;
                    const x = -len + (DESIGN_W + len * 2) * k + r() * 200;
                    const g = ctx.createLinearGradient(x, 0, x + len, 0);
                    g.addColorStop(0, rgba(th.primary, 0));
                    g.addColorStop(1, rgba(th.accent, 0.7));
                    ctx.fillStyle = g;
                    ctx.fillRect(x, y, len, 2 + r() * 2);
                }
                ctx.restore();
            });
        // Right column: label/value pairs decoding in, only where it cannot collide with the copy.
        const log = props.log ?? [];
        if (log.length && widest < 1050) {
            ctx.font = font(env.brand, 500, 28, 'mono');
            ctx.letterSpacing = '1px';
            ctx.textAlign = 'right';
            const right = DESIGN_W - 170;
            const top = DESIGN_H / 2 - 150;
            const logOut = kit.beat.seg(env, L - 0.7, L - 0.25, kit.curves.launch);
            ctx.globalAlpha = 1 - logOut;
            log.forEach(([label, value], i) => {
                const k = kit.beat.seg(env, 0.75 + i * 0.5, 1.15 + i * 0.5);
                if (k <= 0)
                    return;
                const y = top + i * 58 - logOut * 60;
                ctx.fillStyle = rgba(th.fg, 0.62);
                ctx.fillText(kit.text.decode(label, k, env.t, { seed: i + 1 }), right - 120, y);
                ctx.fillStyle = th.accentText;
                ctx.fillText(kit.text.decode(value, k, env.t, { seed: i + 9, charset: '0123456789' }), right, y);
                ctx.fillStyle = rgba(th.fg, 0.12);
                ctx.fillRect(right - 420, y + 16, 420 * k, 1);
            });
            ctx.globalAlpha = 1;
            ctx.textAlign = 'left';
            ctx.letterSpacing = '0px';
        }
    },
});
